/**
 * OAuth-onboarding-stash (docs/onboarding-plaan.md §6, (c-jääk)) - hoiab avaliku
 * „Alusta"-funneli valiku (mall / alamdomeen / ärinimi / capabilities / brief)
 * üle Google-signup'i redirect-ringi.
 *
 * MIKS: Google-signup on account-first - kasutaja lahkub `account.<base>/signup?...`
 * lehelt Google'i juurde (`/api/auth/google`), mispeale funneli query-kontekst KAOB
 * URL-ist; konto luuakse ILMA saidita ja kasutaja maandub `?authOk=google&welcome=1`-ga
 * tagasi. sessionStorage on SAMA-PÄRITOLU (account.<base>) → püsib redirect-ringi üle,
 * seega stash'ime konteksti ENNE lahkumist ja rakendame maandumisel (main.tsx →
 * admin/sites/new). admin.<base> on ERI päritolu (ei näe sessionStorage'i) → seega
 * edastab main.tsx stash'i admin/sites/new-le URL-parameetritena.
 *
 * Aegub (`MAX_AGE_MS`), et vana valik ei „ärkaks" hilisemal login'il; tarbimisel
 * kustutatakse (`clearStash`). Parim-pingutus: privaatrežiim / kvoot → vaikselt eira.
 */
export interface OnboardingStash {
  /** Valmiskujunduse id (tee 2). */
  template?: string;
  /** Alamdomeeni silt (a-z0-9-). */
  subdomain?: string;
  /** Ärinimi (saidi kuvanimi). */
  siteName?: string;
  /** Kas külastaja NÄGI capabilities-sammu (`onboard=1`) - säilita `[]` vs `undefined` vahe. */
  onboard?: boolean;
  /** Valitud võimekused „blog,forms" (võib olla tühi kui onboard && vahele jäetud). */
  caps?: string;
  /** Vabateksti äri-brief (≤400 tm). */
  brief?: string;
  /** Funnelis valitud paketi kood (nt "standard") - Google-teel kaob query URL-ist,
   *  seega kanname stash'iga läbi → esimese saidi loomise järel Stripe checkout. */
  plan?: string;
  /** Arveldustsükkel paketi juurde. */
  cycle?: "monthly" | "yearly";
  /** Talletamise ajatempel (ms) - vananemis-kontrolliks. */
  ts: number;
}

const KEY = "wa_onboarding_stash";
// 15 min - Google-redirect-ring kestab sekundeid (isegi 2FA-ga minuteid), seega
// lühike aken katab legitiimse voo aga kahandab jagatud-tab'i riski: kui kasutaja A
// jätab Google-signup'i pooleli ja B kasutab sama tab'i, saab B A valiku (sh brief-PII)
// ainult selle akna jooksul (review-leid #2). Täislahendus = serveripoolne stash-token §6.4.
const MAX_AGE_MS = 15 * 60 * 1000;

/** Talleta funneli-kontekst (kui midagi sisukat on) enne Google-suunamist. */
export function stashOnboarding(data: Omit<OnboardingStash, "ts">): void {
  // Ainult kui on midagi säilitamisväärset (mall / alamdomeen / capabilities-samm /
  // brief / valitud pakett).
  if (!data.template && !data.subdomain && !data.onboard && !data.brief && !data.plan) return;
  try {
    const clean: OnboardingStash = { ts: Date.now() };
    if (data.template) clean.template = data.template;
    if (data.subdomain) clean.subdomain = data.subdomain;
    if (data.siteName) clean.siteName = data.siteName;
    if (data.onboard) clean.onboard = true;
    if (data.caps) clean.caps = data.caps;
    if (data.brief) clean.brief = data.brief;
    if (data.plan) clean.plan = data.plan;
    if (data.cycle) clean.cycle = data.cycle;
    sessionStorage.setItem(KEY, JSON.stringify(clean));
  } catch {
    /* privaatrežiim / kvoot - stash on parim-pingutus */
  }
}

/** Loe stash kui olemas JA värske (mitte aegunud). Ei kustuta (vt `clearStash`). */
export function readFreshStash(): OnboardingStash | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as OnboardingStash;
    if (!s || typeof s.ts !== "number" || Date.now() - s.ts > MAX_AGE_MS) return null;
    return s;
  } catch {
    return null;
  }
}

/** Kustuta stash (tarbimisel VÕI kui teine tee lõi saidi juba). */
export function clearStash(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* eira */
  }
}
